import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import FormItem from "app/components/inputs/FormItem";
import { durationCustomStyles, DropdownIndicator } from "app/components/PaymentSelector";
import { errorMessageFor } from "./helpers";
import styles from "./styles.module.scss";

interface PackageSelectProps {
  availablePackages: any[];
  paymentEditable: boolean;
}

export default function PackageSelect({ availablePackages, paymentEditable }: PackageSelectProps) {
  const { control, formState: { errors } } = useFormContext();
  const { t } = useTranslation();

  const availableSessions = (pkg: any) => (pkg.unlimited ? '\u221E' : pkg.remainingSessions);

  return (
    <FormItem className={styles.input_field} error={errorMessageFor(errors, 'selected_package_id')}>
      <Controller
        control={control}
        name="selected_package_id"
        shouldUnregister={true}
        // The package is required only while the method is "package". A change
        // to another method clears the package with validation, and that check
        // can end after this select unmounts. A plain `required` then leaves an
        // error on a field that is not on the form, and Save does nothing.
        rules={{
          validate: (value, values) =>
            values.payment_method !== "package" ||
            (value !== "" && value != null) ||
            t("This field is required"),
        }}
        render={({ field }) => {
          // CN-2483: keep the package the booking already uses selectable even when
          // it's fully used (0 remaining); otherwise editing such a booking renders
          // an empty selector. Other exhausted packages stay hidden.
          const selectedRateLinkId = field.value === "" || field.value == null ? null : Number(field.value);
          const options = (availablePackages || [])
            .filter(pkg => pkg.remainingSessions > 0 || pkg.unlimited || pkg.rateLinkId === selectedRateLinkId)
            .map(pkg => ({
              value: pkg.rateLinkId,
              label: `${pkg.productTitle} / ${availableSessions(pkg)} ${t("remaining sessions")}`,
            }));
          const selectedOption = options.find(opt => opt.value === selectedRateLinkId) || null;

          return (
            <Select
              {...field}
              value={selectedOption}
              onChange={(option: any) => field.onChange(option?.value || "")}
              options={options}
              styles={durationCustomStyles}
              placeholder={t("Select Package")}
              components={{ IndicatorSeparator: () => null, DropdownIndicator }}
              isSearchable={false}
              isDisabled={!paymentEditable}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              classNamePrefix="rs"
            />
          );
        }}
      />
    </FormItem>
  );
}
